import {
	AssignStatus,
	ArchivedView,
	ListContentFilter,
	ListOwnership,
	ListRelevanceMode,
} from "@/enum/list.enum";

import { RequestPaginationParams } from "@/types/request-params";
import { SortParams } from "@/types/types";

/**
 * Lean lists query params. `assignStatus`, when provided, marks the lists that have at least one
 * relevant `list_person` for the calling surface (Inbox / ReadyToSend / Outbox). `relevanceMode`
 * decides what happens to the rest: `FILTER` (the default) drops them, `RANK` returns them sorted
 * last so the scope filter can explain why they hold nothing here (PREC-3834).
 */
/**
 * The single value of the overview owner marker (PREC-4892). Every writer of the overview slice —
 * the Lead Lists builder, the Campaigns table, the slice helper — compares against this constant,
 * so a typo cannot silently disable ownership.
 */
export const OVERVIEW_REQUEST_SCOPE = "overview" as const;

export interface LeanListsRequestParams extends RequestPaginationParams {
	assignStatus?: AssignStatus[];
	relevanceMode?: ListRelevanceMode;
	/**
	 * Client-only owner marker (PREC-4892): only the Lead Lists / Campaigns overview's own requests
	 * carry it, so a picker's `getLeanLists` response cannot overwrite the overview slice. The
	 * endpoint strips it before building the request params.
	 */
	scope?: typeof OVERVIEW_REQUEST_SCOPE;
}

/** The overview filters the Lead Lists request is built from. */
export interface ListOverviewRequestInput {
	page: number;
	query: string;
	sourceType?: string;
	sorting: SortParams;
	archivedView: ArchivedView;
	contentFilter: ListContentFilter;
	ownership: ListOwnership;
}

/**
 * The `getLeanLists` query-string params for one request. The client-only `scope` marker is
 * stripped here (PREC-4892) so it never reaches the API; every other param passes through.
 */
export const buildLeanListsRequest = ({
	scope,
	sorting,
	assignStatus,
	relevanceMode,
	...params
}: LeanListsRequestParams) => ({
	...(params && { ...params }),
	...(sorting && { ...sorting }),
	...(assignStatus?.length && { assignStatus: JSON.stringify(assignStatus) }),
	...(relevanceMode && { relevanceMode }),
});

/**
 * The Lead Lists overview's own `getLeanLists` request. Carrying `scope: "overview"` is what makes
 * the list slice accept the response as its rows (PREC-4131's latest-request guard applies only to
 * these); every other surface keeps calling `getLeanLists` without it and reads its own result.
 */
export const buildListOverviewRequest = ({
	page,
	query,
	sourceType,
	sorting,
	archivedView,
	contentFilter,
	ownership,
}: ListOverviewRequestInput): LeanListsRequestParams => ({
	scope: OVERVIEW_REQUEST_SCOPE,
	page,
	limit: 15,
	searchValue: query,
	sourceType,
	sorting,
	// "Hide" excludes archived lists; "Show" omits the filter so archived lists appear
	// alongside the rest (matches the overview design — Show is not an archived-only view).
	archived: archivedView === ArchivedView.HIDE ? false : undefined,
	hasLeads: contentFilter === ListContentFilter.WITH_LEADS ? true : undefined,
	hasCampaigns: contentFilter === ListContentFilter.WITH_CAMPAIGNS ? true : undefined,
	hasActiveCampaigns: contentFilter === ListContentFilter.WITH_ACTIVE_CAMPAIGNS ? true : undefined,
	ownership: ownership === ListOwnership.ALL ? undefined : ownership,
});
